import React, {
    useCallback, useEffect, useState, useMemo, useContext
} from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import {
    SgPaymentUtils,
    SgSessionStorageService,
    SgSubmissionUtil,
    ADOBE_TOPICS,
    COMMON_TAGGING_PROPERTIES,
    SgCvcUtil
} from 'saga-modules';
import SgPaymentHeader from 'sg-payment-header';
import SgMonthlyDdi from 'sg-monthly-ddi';
import SgPaymentFooter from 'sg-payment-footer';
import SgFailedDownloadPayment from 'sg-failed-download-payment';
import SgProductService from 'sg-product-service';
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { publish } from '@jutro/events';
import { TranslatorContext } from '@jutro/locale';
import { Link, Icon } from '@jutro/components';
import SgQnBLobSpecificUtil from 'sg-qnb-lob-specific-util';
import CommonConstants from 'sg-common-constants';
import metadata from './PaymentPage.metadata.json5';
import messages from './PaymentPage.messages';

function PaymentPage(props) {
    const {
        wizardData: modelVM, updateWizardData: updateData, goNext, steps, jumpTo
    } = props;
    const {
        initialValidation,
        onValidate,
        isComponentValid,
    } = useValidation('PaymentPage');

    const translator = useContext(TranslatorContext);
    const { paymentDetails } = modelVM.bindData;
    const chosenPlan = SgSubmissionUtil.getSelectedPaymentPlan(modelVM);
    modelVM.bindData.value.selectedPaymentPlan = chosenPlan.billingId;
    const paymentIframeCanBeLoaded = SgSessionStorageService.getItem(SgSessionStorageService.KEY.PAYMENT_IFRAME_CAN_BE_LOADED) || false;
    const [isPageInitialized, setPageInitialized] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [showCPA, setShowCPA] = useState(false);
    const [showCPAButtons, setShowCPAButtons] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showFailedDownloadMsg, setShowFailedDownloadMsg] = useState(false);
    const [showDepositDetails, setShowDepositDetails] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [selectedAnnualPlanType, setSelectedAnnualPlanType] = useState(undefined);
    const [isIframeDataLoaded, setIsIFrameDataLoaded] = useState(undefined);

    const paymentType = useMemo(() => {
        return SgPaymentUtils.getPaymentType(chosenPlan);
    }, [chosenPlan]);

    const handleNext = useCallback(async () => {
        setIsLoading(true);
        await SgCvcUtil.updateCvcDetails(modelVM);
        modelVM.value = await LoadSaveService.bindSubmission(modelVM.value);
        updateData(modelVM);
        goNext();
    }, [modelVM, updateData, goNext]);

    const handleMixedNext = useCallback(() => {
        setIsLoading(false);
        setShowDepositDetails(true);
        SgPaymentUtils.loadBarclayCard(modelVM, chosenPlan, handleNext, setShowFailedDownloadMsg);
    }, [chosenPlan, handleNext, modelVM]);

    const onNext = useCallback(async () => {
        if (!isComponentValid) {
            setShowErrors(true);
            return false;
        }
        setIsLoading(true);
        paymentDetails.paymentMethod.value = paymentMethod;
        if (paymentType.isMonthlyUpfrontPayment) {
            SgPaymentUtils.bankAccountValidation(modelVM, chosenPlan, handleMixedNext);
        } else if (paymentType.isMonthlyPayment || paymentType.isAnnualDDPayment) {
            SgPaymentUtils.bankAccountValidation(modelVM, chosenPlan, handleNext);
        } else {
            await handleNext();
        }
        return modelVM;
    }, [isComponentValid, paymentDetails, paymentType, paymentMethod, modelVM, chosenPlan, handleMixedNext, handleNext]);

    useEffect(() => {
        modelVM.bindData.paymentDetails.value = modelVM.bindData.paymentDetails.value || {};
        if (paymentType.isAnnualPayment && paymentIframeCanBeLoaded) {
            SgSessionStorageService.removeItem(SgSessionStorageService.KEY.PAYMENT_IFRAME_CAN_BE_LOADED);
            if (!SgPaymentUtils.shouldShowAnnualPaymentTypeQuestion(chosenPlan)) {
                paymentDetails.paymentMethod.value = CommonConstants.PAYMENT_METHOD_CODES.CREDIT_CARD;
                setPaymentMethod(CommonConstants.PAYMENT_METHOD_CODES.CREDIT_CARD);
                paymentDetails.barclayCardData_Ext.autoRenewal = SgQnBLobSpecificUtil.setAutoRenewal(modelVM);
                publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: COMMON_TAGGING_PROPERTIES.paymentAutoRenewal, value: true });
                SgPaymentUtils.loadBarclayCard(modelVM, chosenPlan, handleNext, setShowFailedDownloadMsg, setShowCPA, setIsIFrameDataLoaded);
            }
        } else { // Monthly
            // reset auto renewal field, just in case uses first selected annual and then get back to monthly
            paymentDetails.barclayCardData_Ext.autoRenewal = undefined;
            publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: COMMON_TAGGING_PROPERTIES.paymentAutoRenewal, value: undefined });
            // Init DDI model
            paymentDetails.bankAccountData.value = paymentDetails.bankAccountData.value || {};
            if (paymentType.isMonthlyUpfrontPayment) { // With Down Payment (Card)
                setPaymentMethod(CommonConstants.PAYMENT_METHOD_CODES.MIXED);
            } else {
                setPaymentMethod(CommonConstants.PAYMENT_METHOD_CODES.DDI);
            }
            setIsLoading(false);
        }
        setPageInitialized(true);
        SgQnBLobSpecificUtil.tagOnPaymentPageEntry(modelVM);
        SgQnBLobSpecificUtil.callDiallerOnPaymentPage(modelVM);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const processBarclayCardResponse = useCallback((useCardForRenewal) => {
        setIsLoading(true);
        setShowCPA(false);
        SgPaymentUtils.getPaymentResponse(modelVM, handleNext, useCardForRenewal === undefined ? false : useCardForRenewal);
    }, [handleNext, modelVM]);

    const getBarclaycardResponse = useCallback((event) => {
        const responseProps = {
            autoRenewal: paymentDetails.barclayCardData_Ext.autoRenewal,
            processBarclayCardResponse,
            event,
            barclayCardData: paymentDetails.barclayCardData_Ext.value
        };
        SgPaymentUtils.handleBarclayCardResponse(responseProps);
    }, [paymentDetails, processBarclayCardResponse]);

    useEffect(() => {
        window.addEventListener('message', getBarclaycardResponse);
        return () => {
            window.removeEventListener('message', getBarclaycardResponse);
            const iFrameElement = document.getElementById('paymentConfirmation');
            if (!_.isNil(iFrameElement)) {
                iFrameElement.remove();
            }
        };
    }, []);

    const onSelectedAnnualPlanTypeChange = useCallback(async (selectedPlanValue) => {
        if (selectedPlanValue !== undefined) {
            setSelectedAnnualPlanType(selectedPlanValue);
            if (chosenPlan.billingId !== selectedPlanValue) {
                SgSessionStorageService.setItem(SgSessionStorageService.KEY.SELECTED_PAYMENT_ID, selectedPlanValue);
                const customQuoteProps = {
                    modelVM,
                    updateData,
                    setQuoting: setIsLoading,
                    coverages: {}
                };
                await SgSubmissionUtil.updatePaymentPlan(customQuoteProps);
            }

            if (selectedPlanValue === CommonConstants.PAYMENT_PLANS_ID.ANNUAL_DD) {
                paymentDetails.bankAccountData.value = paymentDetails.bankAccountData.value || {};
                setPaymentMethod(CommonConstants.PAYMENT_METHOD_CODES.DDI);
                setIsLoading(false);
            } else {
                setIsLoading(true);
                _.set(modelVM, 'bindData.paymentDetails.value', _.omit(modelVM.bindData.paymentDetails.value, 'bankAccountData'));
                SgPaymentUtils.loadBarclayCard(modelVM, chosenPlan, handleNext, setShowFailedDownloadMsg, undefined, setIsIFrameDataLoaded);
                paymentDetails.paymentMethod.value = CommonConstants.PAYMENT_METHOD_CODES.CREDIT_CARD;
                setPaymentMethod(CommonConstants.PAYMENT_METHOD_CODES.CREDIT_CARD);
            }
        }
    }, [chosenPlan, handleNext, modelVM, paymentDetails.bankAccountData, paymentDetails.paymentMethod, updateData]);

    const onIFrameLoad = useCallback(() => {
        if (isIframeDataLoaded) {
            setIsLoading(false);
            setIsIFrameDataLoaded(false);
        }
    }, [isIframeDataLoaded]);

    const iframeComponentContent = useCallback(() => {
        return (
            <iframe
                title="iframe"
                id="barclayCardIframe"
                className="sg-card-iframe"
                name="barclayCardIframe"
                onLoad={onIFrameLoad}
            />
        );
    }, [onIFrameLoad]);

    const showCPAbuttons = useCallback((event) => {
        if (event) {
            event.preventDefault();
        }
        setShowCPAButtons(true);
    }, []);

    const isDDIPayment = useMemo(() => {
        return paymentType.isAnnualDDPayment || paymentType.isMonthlyDDPayment || paymentType.isMonthlyPayment;
    }, [paymentType]);

    const avoidDrivingUninsuredBannerContent = useMemo(() => {
        return (
            <div className="sgAvoidDrivingUninsuredBanner">
                <div className="sgAvoidDrivingUninsuredBannerContent">
                    <div className="sgAvoidDrivingUninsuredHeader">
                        <Icon icon="mi-info" className="sgAvoidDrivingUninsuredHeaderIcon" />
                        <h2 className="sgAvoidDrivingUninsuredHeaderText">{translator(messages.AvoidDrivingUninsuredHeader)}</h2>
                    </div>
                    <div className="sgAvoidDrivingUninsuredSubText">
                        <p>
                            <span>{translator(messages.AvoidDrivingUninsuredSubText)}</span>
                        </p>
                    </div>
                </div>
            </div>
        );
    }, []);

    const goBackToQuote = useCallback(() => {
        const premiumPageIndex = _.findIndex(
            steps,
            ({ stepProps }) => stepProps.isQuoteStep
        );
        jumpTo(premiumPageIndex);
    }, [jumpTo, steps]);

    const getContinuousPaymentAuthorityText4Content = useMemo(() => {
        return (
            <>
                {translator(messages.RenewAutomaticallySubText4)}
                &nbsp;
                <Link
                    href="/"
                    id="insuranceDeclinedMoreHelpText"
                    onClick={showCPAbuttons}
                >
                    {translator(messages.RenewAutomaticallySubText5)}
                </Link>
                &nbsp;
                {translator(messages.RenewAutomaticallySubText6)}
            </>
        );
    }, []);

    const showAvoidDrivingUninsuredBanner = useCallback(() => {
        if (paymentDetails.barclayCardData_Ext) {
            return !paymentDetails.barclayCardData_Ext.autoRenewal && !paymentType.isMonthlyUpfrontPayment
                && (SgProductService.isCar() || SgProductService.isMotorHome());
        }
        return false;
    }, [paymentDetails.barclayCardData_Ext]);

    const handleAutoRenewal = useCallback((value) => {
        const iFrameElement = document.getElementById('paymentConfirmation');
        if (!_.isNil(iFrameElement)) {
            iFrameElement.remove();
        }
        const newModelVM = _.clone(modelVM);
        _.set(newModelVM, 'bindData.paymentDetails.barclayCardData_Ext.autoRenewal', value);
        updateData(newModelVM);
        SgPaymentUtils.loadBarclayCard(newModelVM, chosenPlan, handleNext, setShowFailedDownloadMsg, setShowCPA);
    }, [chosenPlan, handleNext, modelVM, updateData]);

    const commonProps = {
        modelVM,
        updateData,
        showErrors: showErrors,
    };

    const isContentVisible = useCallback(() => {
        if (!SgPaymentUtils.shouldShowAnnualPaymentTypeQuestion(chosenPlan)) {
            return true;
        }
        return !_.isNil(selectedAnnualPlanType);
    }, [chosenPlan, selectedAnnualPlanType]);

    const overrideProps = {
        paymentPageContentContainer: {
            visible: isContentVisible() ? '' : false,
        },
        paymentHeaderSection: {
            paymentType,
            chosenPlan,
            showCPA,
            showDepositDetails,
            ...commonProps
        },
        monthlyDdiSection: {
            ...commonProps,
            visible: (paymentType.isMonthlyUpfrontPayment && !showDepositDetails) || isDDIPayment,
            controller: SgMonthlyDdi
        },
        iframeContainer: {
            content: iframeComponentContent(),
            visible: ((paymentType.isAnnualPayment && !paymentType.isAnnualDDPayment) || showDepositDetails || paymentType.isAnnualUpfrontPayment)
                ? '' : false
        },
        detailsMixedNavigationTitle: {
            visible: paymentType.isMonthlyUpfrontPayment && !showCPA && !showDepositDetails ? '' : false
        },
        paymentContinueContainer: {
            visible: ((paymentType.isMonthlyUpfrontPayment && !showDepositDetails) || isDDIPayment) ? '' : false
        },
        continuousPaymentAuthoritySection: {
            visible: showCPA ? '' : false
        },
        continuousPaymentAuthorityText4: {
            content: getContinuousPaymentAuthorityText4Content
        },
        continuousPaymentAuthorityButtonsSection: {
            visible: showCPAButtons ? '' : false
        },
        avoidDrivingUninsuredBanner: {
            content: avoidDrivingUninsuredBannerContent,
            className: 'sgAvoidDrivingUninsuredContainer',
            visible: showAvoidDrivingUninsuredBanner() ? '' : false
        },
        failedDownloadMessage: {
            showFailedDownloadMsg: showFailedDownloadMsg && !showCPA
        },
        paymentFooterSection: {
            visible: (isContentVisible() && (isDDIPayment || (paymentType.isMonthlyUpfrontPayment && !showDepositDetails))) ? '' : false
        },
        backButton: {
            disabled: isLoading
        },
        continueButton: {
            disabled: isLoading
        },
        selectHowYouWouldLikeToPay: {
            disabled: !_.isNil(selectedAnnualPlanType),
            visible: SgPaymentUtils.shouldShowAnnualPaymentTypeQuestion(chosenPlan) ? '' : false,
            value: selectedAnnualPlanType,
            onValueChange: (value) => {
                onSelectedAnnualPlanTypeChange(value);
            }
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate: onValidate,
            onNext: onNext,
            goBackToQuote: goBackToQuote,
            handleAutoRenewal

        },
        resolveComponentMap: {
            SgPaymentHeader,
            SgMonthlyDdi,
            SgFailedDownloadPayment,
            SgPaymentFooter
        },
    };

    if (!isPageInitialized) {
        return null;
    }

    return (
        <WizardPage
            showNext={false}
            showPrevious={false}
            skipWhen={initialValidation}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={modelVM}
                overrideProps={overrideProps}
                onModelChange={updateData}
                onValidationChange={onValidate}
                showErrors={showErrors}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

PaymentPage.propTypes = wizardProps;
export default PaymentPage;
